import type { Song } from "./Song";

/** One version of a song. Its lyrics and chords are in the file `chord-sheets/<id>.chordpro`. */
export type ChordSheet = {
    id: string;
    songId: string;
    /** Empty for a song's first chord sheet, which is just called after the song. */
    name: string;
    /** The chord sheet this one was copied from, when saved as a new version. */
    copiedFromId?: string;
    /** Set when this chord sheet belongs to one setlist only. */
    setlistId?: string;
    changedAt: string;
};

/**
 * The most a chord sheet's file may hold, in bytes. Even a long song is about 15 KB of text, so this only stops
 * things that aren't chord sheets, like PDFs or photos, and keeps shared setlists small.
 */
export const MAX_CHORD_SHEET_BYTES = 64 * 1024;

/** Whether ChordPro text is more than a chord sheet may hold. */
export function chordSheetTooBig(text: string): boolean {
    return new TextEncoder().encode(text).length > MAX_CHORD_SHEET_BYTES;
}

/** Where the file of a chord sheet is stored. */
export function chordSheetPath(id: string): string {
    return `chord-sheets/${id}.chordpro`;
}

/** Sorts a song's chord sheets: the first one, then the others by name. */
export function compareChordSheets(a: ChordSheet, b: ChordSheet): number {
    if (a.name === "") return -1;
    if (b.name === "") return 1;

    return a.name.localeCompare(b.name);
}

/** The name people see for a chord sheet: its song, plus its own name if it has one. */
export function chordSheetTitle(chordSheet: ChordSheet, song: Song): string {
    return chordSheet.name ? `${song.title}, ${chordSheet.name}` : song.title;
}
